import { type ReactNode, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import type {
  ScopeAttentionFilterContext as ScopeAttentionFilterEvalContext,
  ScopeFilterKey,
} from '@/Client/Lib/scopeAttentionFilterLib';
import type { OverallTaskStatus } from '@kaiizen/kai-pro-api';

import { ScopeAttentionFilterContext } from './ScopeAttentionFilterContext';
import {
  clearScopeAttentionSelectionFromSearchParams,
  readScopeAttentionFiltersFromSearchParams,
  readScopeAttentionTaskStatusFromSearchParams,
  toggleScopeAttentionFilterInSearchParams,
  toggleScopeAttentionTaskStatusInSearchParams,
} from './scopeAttentionFilterUrl';

const EMPTY_EVAL_CONTEXT: ScopeAttentionFilterEvalContext = {};

export const ScopeAttentionFilterProvider = ({
  children,
  evalContext = EMPTY_EVAL_CONTEXT,
}: {
  children: ReactNode;
  /** Evaluation inputs for context-dependent filters (e.g. contractor resolver). */
  evalContext?: ScopeAttentionFilterEvalContext;
}) => {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const activeTaskStatus = useMemo(() => readScopeAttentionTaskStatusFromSearchParams(searchParams), [searchParams]);
  const activeFilters = useMemo(
    () => activeTaskStatus ? new Set<ScopeFilterKey>() : readScopeAttentionFiltersFromSearchParams(searchParams),
    [activeTaskStatus, searchParams],
  );
  const effectiveEvalContext = useMemo(
    () => ({ ...evalContext, taskStatus: activeTaskStatus }),
    [activeTaskStatus, evalContext],
  );

  const navigateWithParams = useCallback((next: URLSearchParams) => {
    const search = next.toString();
    navigate({ pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [navigate, pathname]);

  const toggleFilter = useCallback(
    (filter: ScopeFilterKey) => navigateWithParams(toggleScopeAttentionFilterInSearchParams(searchParams, filter)),
    [navigateWithParams, searchParams],
  );

  const toggleTaskStatus = useCallback(
    (status: OverallTaskStatus) => navigateWithParams(toggleScopeAttentionTaskStatusInSearchParams(searchParams, status)),
    [navigateWithParams, searchParams],
  );

  const clearFilters = useCallback(
    () => navigateWithParams(clearScopeAttentionSelectionFromSearchParams(searchParams)),
    [navigateWithParams, searchParams],
  );

  const contextValue = useMemo(
    () => ({ activeFilters, activeTaskStatus, evalContext: effectiveEvalContext, toggleFilter, toggleTaskStatus, clearFilters }),
    [activeFilters, activeTaskStatus, clearFilters, effectiveEvalContext, toggleFilter, toggleTaskStatus],
  );

  return (
    <ScopeAttentionFilterContext.Provider value={contextValue}>
      {children}
    </ScopeAttentionFilterContext.Provider>
  );
};
